import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Plus, GripVertical, MapPin, Phone, Mail, Clock, Trash2, PlayCircle, Images, Link as LinkIcon } from 'lucide-react';
import { SortableList } from './SortableList';
import { ItemActions } from './ItemActions';

const BranchesManager = () => {
  const [branches, setBranches] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchBranches = async () => {
    const res = await adminApi.get('/branches');
    setBranches(res.data || []);
  };
  useEffect(() => { fetchBranches(); }, []);

  const blank = () => ({
    name: '',
    address: '',
    phone: '',
    email: '',
    hours: '',
    map_url: '',
    video_url: '',
    gallery_urls: [],
    order: branches.length,
    published: true,
  });

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const cleaned = {
        ...editing,
        gallery_urls: (editing.gallery_urls || []).map((u) => (u || '').trim()).filter(Boolean),
      };
      if (cleaned.id && branches.find((x) => x.id === cleaned.id)) {
        await adminApi.put(`/branches/${cleaned.id}`, cleaned);
      } else {
        await adminApi.post('/branches', { ...cleaned, id: undefined });
      }
      toast({ title: 'Saved' });
      setEditing(null);
      fetchBranches();
    } catch (err) {
      toast({ title: 'Error', description: 'Save failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this branch?')) return;
    await adminApi.delete(`/branches/${id}`);
    fetchBranches();
  };

  const handleReorder = async (reordered) => {
    setBranches(reordered);
    try { await adminApi.put('/branches/reorder', { ids: reordered.map((r) => r.id) }); } catch {}
  };

  return (
    <div className="space-y-6" data-testid="branches-manager">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">EWRF Branches</h2>
        <Button onClick={() => setEditing(blank())} className="bg-[#1565C0]" data-testid="add-branch-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Branch
        </Button>
      </div>

      {editing && (
        <Card>
          <CardHeader><CardTitle>{editing.id ? 'Edit Branch' : 'New Branch'}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <Label htmlFor="branch-name">Branch Name</Label>
                <Input id="branch-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} required data-testid="branch-name-input" placeholder="EWRF HQ — Kuala Lumpur" />
              </div>
              <div>
                <Label htmlFor="branch-address">Address</Label>
                <Textarea id="branch-address" value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} rows={3} />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="branch-phone">Phone</Label>
                  <Input id="branch-phone" value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} placeholder="03-7498 7140" />
                </div>
                <div>
                  <Label htmlFor="branch-email">Email</Label>
                  <Input id="branch-email" type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} placeholder="branch@ewrf.org.my" />
                </div>
              </div>
              <div>
                <Label htmlFor="branch-hours">Opening Hours</Label>
                <Input id="branch-hours" value={editing.hours} onChange={(e) => setEditing({ ...editing, hours: e.target.value })} placeholder="Mon-Fri 9am - 6pm" />
              </div>
              <div>
                <Label htmlFor="branch-map">Google Maps URL (optional)</Label>
                <Input id="branch-map" value={editing.map_url} onChange={(e) => setEditing({ ...editing, map_url: e.target.value })} placeholder="https://maps.google.com/..." />
              </div>
              <div>
                <Label htmlFor="branch-video" className="flex items-center gap-1.5">
                  <PlayCircle className="w-4 h-4 text-rose-600" />
                  Video URL (YouTube / Vimeo / .mp4 — optional)
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="branch-video"
                    value={editing.video_url || ''}
                    onChange={(e) => setEditing({ ...editing, video_url: e.target.value })}
                    placeholder="https://youtu.be/... or .mp4 URL"
                    data-testid="branch-video-input"
                  />
                  {editing.video_url && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setEditing({ ...editing, video_url: '' })}
                      className="text-red-600 shrink-0"
                      data-testid="branch-video-clear"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <Label className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Images className="w-4 h-4 text-amber-600" />
                    Photo Gallery ({(editing.gallery_urls || []).length})
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing({
                      ...editing,
                      gallery_urls: [...(editing.gallery_urls || []), ''],
                    })}
                    data-testid="branch-gallery-add"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Photo URL
                  </Button>
                </Label>
                <div className="space-y-2 mt-2" data-testid="branch-gallery-list">
                  {(editing.gallery_urls || []).length === 0 && (
                    <p className="text-xs text-gray-400 italic">No photos yet. Click &quot;Add Photo URL&quot; to add one.</p>
                  )}
                  {(editing.gallery_urls || []).map((url, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`branch-gallery-row-${i}`}>
                      <span className="text-xs text-gray-400 w-5 text-right">{i + 1}.</span>
                      <LinkIcon className="w-4 h-4 text-gray-400 shrink-0" />
                      <Input
                        value={url}
                        onChange={(e) => {
                          const next = [...(editing.gallery_urls || [])];
                          next[i] = e.target.value;
                          setEditing({ ...editing, gallery_urls: next });
                        }}
                        placeholder="/api/uploads/photo.jpg or https://..."
                        data-testid={`branch-gallery-url-${i}`}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const next = [...(editing.gallery_urls || [])];
                          next.splice(i, 1);
                          setEditing({ ...editing, gallery_urls: next });
                        }}
                        className="text-red-600 shrink-0"
                        data-testid={`branch-gallery-remove-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-gray-500 mt-2">
                  Paste image URLs — either uploaded assets (starting with <code>/api/uploads/…</code>) or full https URLs.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="branch-published" checked={editing.published !== false} onCheckedChange={(v) => setEditing({ ...editing, published: v })} />
                <Label htmlFor="branch-published">Published</Label>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={loading} className="bg-[#1565C0]" data-testid="save-branch-btn">{loading ? 'Saving…' : 'Save'}</Button>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <SortableList
        items={branches}
        onReorder={handleReorder}
        renderItem={(b) => (
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold mb-1">{b.name}</h3>
                <div className="text-xs text-gray-600 space-y-0.5">
                  {b.address && <div className="flex items-start gap-1"><MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" /> <span className="truncate">{b.address}</span></div>}
                  {b.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" /> {b.phone}</div>}
                  {b.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {b.email}</div>}
                  {b.hours && <div className="flex items-center gap-1"><Clock className="w-3 h-3" /> {b.hours}</div>}
                </div>
              </div>
              <ItemActions
                collection="branches"
                item={b}
                onChanged={fetchBranches}
                onEdit={setEditing}
                onDelete={handleDelete}
              />
            </CardContent>
          </Card>
        )}
      />
      {branches.length === 0 && !editing && <p className="text-center text-gray-500 py-8">No branches yet. Click "Add Branch" to create one.</p>}
    </div>
  );
};

export default BranchesManager;
